/**
 * Auth context — thin wrapper over Firebase Authentication.
 *
 * Exposes the current user, a resolving flag, and the sign-in/up/out actions
 * used by LoginPage and the app shell. Auth state is the single source of
 * truth via onAuthStateChanged.
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signOut as fbSignOut,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';

const AuthContext = createContext(null);

// Map Firebase error codes to human-friendly messages.
const FRIENDLY = {
  'auth/invalid-email': 'That email address looks invalid.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/email-already-in-use': 'An account with that email already exists.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/popup-blocked': 'Popup blocked — allow popups and try again.',
  'auth/network-request-failed': 'Network error — check your connection.',
};
const friendly = (err) => FRIENDLY[err?.code] || err?.message || 'Something went wrong. Please try again.';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
    return unsub;
  }, []);

  const signIn = useCallback(async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      return { user: cred.user };
    } catch (err) {
      return { error: friendly(err) };
    }
  }, []);

  const signUp = useCallback(async (email, password, displayName) => {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        try { await updateProfile(cred.user, { displayName }); } catch { /* non-fatal */ }
      }
      return { user: cred.user };
    } catch (err) {
      return { error: friendly(err) };
    }
  }, []);

  const signInWithGoogle = useCallback(async () => {
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      return { user: cred.user };
    } catch (err) {
      return { error: friendly(err) };
    }
  }, []);

  const signOut = useCallback(() => fbSignOut(auth), []);

  const value = { user, loading, signIn, signUp, signInWithGoogle, signOut };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
